import styled from 'styled-components';
import { ButtonConfirm } from '../../../../components/ButtonConfirm';
import { colors, radius } from '../../../../styles/tokens';

export const Container = styled.div`
  padding: 40px;
  min-height: 100%;

  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 1199px) {
    padding: 20px;
  }
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

export const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 800;
  color: ${colors.onSurface};
  letter-spacing: -0.01em;
  margin-bottom: 4px;
`;

export const PageSubtitle = styled.p`
  font-size: 14px;
  color: ${colors.onSurfaceVariant};
`;

export const Layout = styled.div`
  flex: 1;

  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 20px;
  align-items: start;

  @media (max-width: 1199px) {
    grid-template-columns: 1fr;
  }
`;

export const ListColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const PlanRow = styled.button<{ active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;

  width: 100%;
  padding: 16px;
  border: none;
  border-radius: ${radius.md};
  cursor: pointer;
  text-align: left;

  background-color: ${({ active }) =>
    active ? colors.surfaceContainerHigh : colors.surfaceContainerLow};
  outline: ${({ active }) => (active ? `2px solid ${colors.primary}` : 'none')};
  outline-offset: -2px;
`;

export const PlanRowName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${colors.onSurface};
  text-transform: capitalize;
`;

export const PlanRowPrice = styled.span`
  font-size: 12px;
  color: ${colors.onSurfaceVariant};
`;

export const DetailColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const Panel = styled.div`
  padding: 24px;
  border-radius: ${radius.md};
  background-color: ${colors.surfaceContainerLow};
`;

export const PanelTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: ${colors.onSurface};
  margin-bottom: 4px;
`;

export const PanelSubtitle = styled.p`
  font-size: 12px;
  color: ${colors.onSurfaceVariant};
  margin-bottom: 20px;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: end;

  @media (max-width: 1199px) {
    grid-template-columns: 1fr;
  }
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: 700;
  color: ${colors.onSurfaceVariant};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const fieldStyle = `
  height: 42px;
  padding: 0 14px;

  background-color: ${colors.surfaceContainer};
  border: none;
  border-radius: ${radius.DEFAULT};

  font-size: 13px;
  color: ${colors.onSurface};
`;

export const TextInput = styled.input`
  ${fieldStyle}

  /* Remove as setas do input numérico (Chrome/Safari/Edge e Firefox). */
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export const TextArea = styled.textarea`
  ${fieldStyle}
  height: auto;
  min-height: 42px;
  padding-top: 10px;
  resize: vertical;
`;

export const SaveButton = styled(ButtonConfirm)`
  width: auto;
  height: 42px;
  padding: 0 20px;
  border-radius: ${radius.DEFAULT};
`;

export const FeatureGroupTitle = styled.h3`
  font-size: 12px;
  font-weight: 700;
  color: ${colors.onSurfaceVariant};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 20px 0 10px;

  &:first-child {
    margin-top: 0;
  }
`;

export const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const FeatureToggleRow = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  padding: 12px 4px;
  border-bottom: 1px solid ${colors.surfaceContainerHigh}80;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }
`;

export const FeatureInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const FeatureName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.onSurface};
`;

export const FeatureCode = styled.span`
  font-size: 11px;
  color: ${colors.onSurfaceVariant};
  font-family: monospace;
`;

export const ToggleSwitch = styled.input`
  appearance: none;
  width: 40px;
  height: 22px;
  border-radius: ${radius.full};
  background-color: ${colors.surfaceContainerHigh};
  position: relative;
  cursor: pointer;
  flex-shrink: 0;

  transition: background-color 0.15s;

  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background-color: ${colors.onSurfaceVariant};
    transition: transform 0.15s, background-color 0.15s;
  }

  &:checked {
    background-color: ${colors.primary};
  }

  &:checked::before {
    transform: translateX(18px);
    background-color: ${colors.onPrimary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const NumericRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  padding: 12px 4px;
  border-bottom: 1px solid ${colors.surfaceContainerHigh}80;

  &:last-child {
    border-bottom: none;
  }
`;

export const ValueCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const ValueInput = styled.input`
  width: 90px;
  height: 34px;
  padding: 0 10px;

  background-color: ${colors.surfaceContainer};
  border: none;
  border-radius: ${radius.DEFAULT};

  font-size: 13px;
  color: ${colors.onSurface};

  &:disabled {
    opacity: 0.4;
  }

  /* Remove as setas do input numérico (Chrome/Safari/Edge e Firefox). */
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export const UnlimitedLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;

  font-size: 11px;
  color: ${colors.onSurfaceVariant};
  cursor: pointer;
  white-space: nowrap;
`;

export const RowSaveButton = styled.button<{ dirty: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 34px;
  height: 34px;
  border: none;
  border-radius: ${radius.DEFAULT};
  cursor: pointer;

  background-color: ${({ dirty }) =>
    dirty ? colors.primary : colors.surfaceContainer};
  color: ${({ dirty }) => (dirty ? colors.onPrimary : colors.onSurfaceVariant)};

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const EmptyDetail = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;

  border-radius: ${radius.md};
  background-color: ${colors.surfaceContainerLow};

  font-size: 13px;
  color: ${colors.onSurfaceVariant};
`;
